
import { BaseRepository } from '@/shared/infrastructure/BaseRepository';

interface BigFiveResultData {
  id: string;
  userId: string;
  openness: number;
  conscientiousness: number;
  extraversion: number;
  agreeableness: number;
  neuroticism: number;
  createdAt: Date;
  updatedAt: Date;
}

interface CreateBigFiveResultData {
  userId: string;
  openness: number;
  conscientiousness: number;
  extraversion: number;
  agreeableness: number;
  neuroticism: number;
}

export class PersonalityRepository extends BaseRepository {
  
  async findById(id: string): Promise<BigFiveResultData | null> {
    const { data, error } = await this.getClient()
      .from('big_five_results')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error(`Erro ao buscar resultado por ID: ${error.message}`);
      return null;
    }

    return data ? this.mapToBigFiveResultData(data) : null;
  }

  async findByUserId(userId: string): Promise<BigFiveResultData[]> {
    const { data, error } = await this.getClient()
      .from('big_five_results')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Erro ao buscar resultados por usuário: ${error.message}`);
    }

    return (data || []).map(item => this.mapToBigFiveResultData(item));
  }

  async create(data: CreateBigFiveResultData): Promise<BigFiveResultData> {
    const { data: createdData, error } = await this.getClient()
      .from('big_five_results')
      .insert({
        user_id: data.userId,
        openness: data.openness,
        conscientiousness: data.conscientiousness,
        extraversion: data.extraversion,
        agreeableness: data.agreeableness,
        neuroticism: data.neuroticism
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Erro ao criar resultado: ${error.message}`);
    }

    return this.mapToBigFiveResultData(createdData);
  }

  async getResultsHistory(userId: string, limit?: number): Promise<BigFiveResultData[]> {
    let query = this.getClient()
      .from('big_five_results')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Erro ao buscar histórico de resultados: ${error.message}`);
    }

    return (data || []).map(item => this.mapToBigFiveResultData(item));
  }

  private mapToBigFiveResultData(data: any): BigFiveResultData {
    return {
      id: data.id,
      userId: data.user_id,
      openness: data.openness,
      conscientiousness: data.conscientiousness,
      extraversion: data.extraversion,
      agreeableness: data.agreeableness,
      neuroticism: data.neuroticism,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at)
    };
  }
}
